import { useEffect, useMemo, useState } from "react";
import { GREEN_STATIONS } from "../stations/greenStations";
import { PURPLE_STATIONS } from "../stations/purpleStations";
import { YELLOW_STATIONS } from "../stations/yellowStations";
import yellowData from "../stations/yellowSchedule.json";
import purpleData from "../stations/purpleSchedule.json";
import greenData from "../stations/greenSchedule.json";
import { t as i18n } from "../i18n";

export default function Favorites({ theme = "light", language = "en" }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const central = i18n(language);
  const T = {
    favourites: central.favourites,
    noFav: central.noFav,
    addFav: central.addFav,
    addFavStop: central.addFavStop,
    line: central.line,
    stop: central.stop,
    cancel: central.cancel,
    save: central.save,
    arriving: central.arriving,
    select: central.select,
    purpleLine: central.purpleLine,
    yellowLine: central.yellowLine,
    greenLine: central.greenLine,
  };

  const stopsByLine = useMemo(
    () => ({
      purple: PURPLE_STATIONS,
      yellow: YELLOW_STATIONS,
      green: GREEN_STATIONS,
    }),
    []
  );

  const lineColors = {
    purple: "#800080",
    yellow: "#EAB308",
    green: "#16A34A",
  };

  const cardSize = {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  };

  const getStationLabel = (station) => {
    if (!station) return "";
    if (language === "hi") return station.name_hi || station.name;
    if (language === "kn") return station.name_kn || station.name;
    return station.name;
  };

  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem("favorites");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("favorites", JSON.stringify(favorites));
  }, [favorites]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState("purple");
  const [selectedStop, setSelectedStop] = useState("");
  const [selectedTowards, setSelectedTowards] = useState("");

  const addFavorite = () => {
    if (!selectedStop || !selectedTowards || favorites.length >= 5) {
      setModalOpen(false);
      return;
    }
    const exists = favorites.some(
      (f) => f.line === selectedLine && f.stop === selectedStop && f.towards === selectedTowards
    );
    if (exists) {
      setModalOpen(false);
      setSelectedStop("");
      setSelectedTowards("");
      return;
    }
    setFavorites([...favorites, { line: selectedLine, stop: selectedStop, towards: selectedTowards }]);
    setModalOpen(false);
    setSelectedStop("");
    setSelectedTowards("");
  };

  const removeFavorite = (favToRemove) => {
    setFavorites(
      favorites.filter(
        (f) =>
          !(
            f.line === favToRemove.line &&
            f.stop === favToRemove.stop &&
            f.towards === favToRemove.towards
          )
      )
    );
  };

  // ==== ETA + Absolute arrival time (IST) ====
  const [etas, setEtas] = useState({});

  const IST_OFFSET_MIN = 330;
  const IST_OFFSET_MS = IST_OFFSET_MIN * 60 * 1000;
  const DAY_MS = 24 * 60 * 60 * 1000;

  const hhmmssToMs = (t) => {
    const [hh, mm, ss] = t.split(":").map((x) => parseInt(x, 10));
    return ((hh * 60 + mm) * 60 + (ss || 0)) * 1000;
  };

  // milliseconds since IST midnight for "now"
  const nowWithinDayIST = () => {
    const n = Date.now() + IST_OFFSET_MS;
    const d = new Date(n);
    return (
      ((d.getUTCHours() * 60 + d.getUTCMinutes()) * 60 + d.getUTCSeconds()) * 1000 +
      d.getUTCMilliseconds()
    );
  };

  // UTC epoch ms for today's IST midnight
  const istMidnightUtcMs = () => {
    const shifted = new Date(Date.now() + IST_OFFSET_MS);
    // Date.UTC gives UTC midnight for that date; subtract offset to get IST midnight in UTC
    return Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), 0, 0, 0, 0) - IST_OFFSET_MS;
  };

  // from a list of "HH:MM:SS" strings, get next arrival minutes and absolute UTC ms
  const getNextArrival = (times) => {
    if (!times || !times.length) return { mins: null, absUtc: null };

    const withinDayList = times
      .map((t) => (typeof t === "string" ? t : t.time))
      .filter(Boolean)
      .map(hhmmssToMs)
      .sort((a, b) => a - b);

    const nowMs = nowWithinDayIST();
    const idx = withinDayList.findIndex((ms) => ms >= nowMs);

    // choose target within-day ms and possible day roll-over
    const targetWithin = idx >= 0 ? withinDayList[idx] : withinDayList[0];
    const dayOffset = idx >= 0 ? 0 : DAY_MS;

    const midnightUtc = istMidnightUtcMs();
    const absUtc = midnightUtc + targetWithin + dayOffset;

    const etaMs = (targetWithin + dayOffset) - nowMs;
    const mins = Math.max(0, Math.ceil(etaMs / 60000));

    return { mins, absUtc };
  };

  const formatRemaining = (minutes) => {
    if (minutes == null) return "";
    if (minutes < 1) return T.arriving;
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hrs} hr${hrs > 1 ? "s" : ""}` : `${hrs} hr${hrs > 1 ? "s" : ""} ${mins} min`;
  };

  const locale =
    language === "hi" ? "hi-IN" : language === "kn" ? "kn-IN" : "en-IN";

  const formatClock = (absUtc) => {
    if (!absUtc) return "--";
    return new Date(absUtc).toLocaleTimeString(locale, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    const compute = () => {
      const result = {};
      for (const fav of favorites) {
        const stations = stopsByLine[fav.line] || [];
        const station = stations.find((s) => s.name === fav.stop);
        const key = `${fav.line}|${fav.stop}|${fav.towards}`;
        if (!station || stations.length === 0) {
          result[key] = null;
          continue;
        }
        const firstName = getStationLabel(stations[0]);
        const lastName = getStationLabel(stations[stations.length - 1]);
        const idx = stations.findIndex((s) => s.name === fav.stop);

        let toLast = null, toFirst = null, toLastAbs = null, toFirstAbs = null;

        if (fav.line === "yellow") {
          const stationId = (station.station_id || "").trim().toUpperCase();
          const up = yellowData?.["UP Line"]?.[stationId]?.trips || [];
          const dn = yellowData?.["DN Line"]?.[stationId]?.trips || [];
          const a = getNextArrival(up);
          const b = getNextArrival(dn);
          toLast = a.mins; toLastAbs = a.absUtc;
          toFirst = b.mins; toFirstAbs = b.absUtc;
        } else if (fav.line === "purple") {
          const stationId = (station.station_id || "").trim().toUpperCase();
          const up = purpleData?.["UP Line"]?.[stationId]?.trips || [];
          const dn = purpleData?.["DN Line"]?.[stationId]?.trips || [];
          const a = getNextArrival(up);
          const b = getNextArrival(dn);
          toLast = a.mins; toLastAbs = a.absUtc;
          toFirst = b.mins; toFirstAbs = b.absUtc;
        } else if (fav.line === "green") {
          const stationId = (station.station_id || "").trim().toUpperCase();
          const up = greenData?.["UP Line"]?.[stationId]?.trips || [];
          const dn = greenData?.["DN Line"]?.[stationId]?.trips || [];
          const a = getNextArrival(up);
          const b = getNextArrival(dn);
          toLast = a.mins; toLastAbs = a.absUtc;
          toFirst = b.mins; toFirstAbs = b.absUtc;
        } else {
          // fallback if a station object had inline schedules
          const a = getNextArrival(station.upSchedule || []);
          const b = getNextArrival(station.downSchedule || []);
          toLast = a.mins; toLastAbs = a.absUtc;
          toFirst = b.mins; toFirstAbs = b.absUtc;
        }

        result[key] = { toFirst, toLast, toFirstAbs, toLastAbs, idx, firstName, lastName };
      }
      setEtas(result);
    };
    compute();
    const id = setInterval(compute, 15000);
    return () => clearInterval(id);
  }, [favorites, stopsByLine, language]);

  // one row showing: direction label on left, clock time then (~ remaining) on right
  const etaRow = (station, minutes) => {
    const labelText = getStationLabel(station);

    if (minutes == null) {
      return (
        <div style={{
          display: "flex",
          alignItems: "baseline",       // align text baselines
          justifyContent: "space-between",
          gap: 8,
          whiteSpace: "nowrap",
          fontSize: 13,
        }}>
          <span style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            → {labelText}
          </span>
          <span style={{ fontWeight: 600 }}>--</span>
        </div>
      );
    }

    // compute "~ ..." text
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const approx =
      minutes < 1
        ? `(${T.arriving})`
        : hrs > 0
          ? mins === 0
            ? `(~ ${hrs} hr${hrs > 1 ? "s" : ""})`
            : `(~ ${hrs} hr${hrs > 1 ? "s" : ""} ${mins} min)`
          : `(~ ${minutes} min)`;

    // clock at IST
    const arrivalUtcMs = Date.now() + minutes * 60_000;
    const timeStr = new Date(arrivalUtcMs).toLocaleTimeString(locale, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });

    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",  // same line: left label, right time
        columnGap: 8,
        rowGap: 2,
        alignItems: "baseline",
        fontSize: 13,
        whiteSpace: "nowrap",
      }}>
        {/* Row 1: arrow+name (left)  |  time (right) */}
        <span style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          → {labelText}
        </span>
        <span style={{ fontWeight: 700, lineHeight: 1.1 }}>{timeStr}</span>

        {/* Row 2: empty cell to keep time aligned | (~ mins) below the time */}
        <span />
        <span style={{ fontSize: 11, opacity: 0.65, textAlign: "center", display: "block", }}>{approx}</span>
      </div>
    );
  };

  // ==== Layout container ====
  const containerStyle = {
    marginBottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    gap: "12px",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "10px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  return (
    <section style={containerStyle}>
      {favorites.length === 0 && (
        <div
          style={{
            flex: "0 0 180px",
            height: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 500,
            color: theme === "dark" ? "#ccc" : "#555",
          }}
        >
          {T.noFav}
        </div>
      )}

      {favorites.map((fav) => {
        const key = `${fav.line}|${fav.stop}|${fav.towards}`;
        const info = etas[key];
        const col = lineColors[fav.line] || "#999";
        const stations = stopsByLine[fav.line] || [];
        const idx = info?.idx ?? stations.findIndex((s) => s.name === fav.stop);
        const firstName = info?.firstName ?? stations[0]?.name ?? "Terminal A";
        const lastName = info?.lastName ?? stations[stations.length - 1]?.name ?? "Terminal B";
        const showToLast = idx > -1 && idx !== stations.length - 1;
        const showToFirst = idx > 0;

        return (
          <div
            key={key}
            style={{
              ...cardSize,
              height: isMobile ? "100px" : "110px",
              background:
                theme === "dark"
                  ? "rgba(28, 28, 28, 0.55)"
                  : "rgba(255, 255, 255, 0.55)",
              color: theme === "dark" ? "#fff" : "#111",
              borderRadius: 16,
              padding: "14px",
              display: "flex",
              flexDirection: "column",
              backdropFilter: "blur(14px) saturate(160%)",
              WebkitBackdropFilter: "blur(14px) saturate(160%)",
              border:
                theme === "dark"
                  ? "1px solid rgba(255,255,255,0.22)"
                  : "1px solid rgba(0,0,0,0.08)",
              boxShadow:
                theme === "dark"
                  ? "0 8px 30px rgba(0,0,0,0.85), 0 0 12px rgba(255,255,255,0.12)"
                  : "0 8px 30px rgba(0,0,0,0.18), 0 0 12px rgba(255,255,255,0.6)",
              transition: "all 0.25s ease",
            }}
          >

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: "4px", height: "18px", borderRadius: "2px", background: col }} />
                <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>
                  {getStationLabel(stations.find((s) => s.name === fav.stop))}
                </span>
              </div>
              <button
                onClick={() => removeFavorite(fav)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: theme === "dark" ? "#aaa" : "#444",
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                ×
              </button>
            </div>

            {/* Direction arrival & remaining */}
            <div style={{ fontSize: 13, marginTop: 8 }}>
              {fav.towards === firstName && showToFirst &&
                etaRow(stations[0], info?.toFirst ?? null, info?.toFirstAbs ?? null)}
              {fav.towards === lastName && showToLast &&
                etaRow(stations[stations.length - 1], info?.toLast ?? null, info?.toLastAbs ?? null)}
            </div>
          </div>
        );
      })}

      {favorites.length < 5 && (
        <div
          onClick={() => setModalOpen(true)}
          style={{
            ...cardSize,
            height: isMobile ? "100px" : "110px",
            border: theme === "dark" ? "2px dashed #555" : "2px dashed #aaa",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: theme === "dark" ? "#fff" : "#111",
            background: theme === "dark" ? "#1e1e1e" : "#fafafa",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500 }}>+ {T.addFav}</span>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div
          onClick={() => setModalOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: theme === "dark" ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.35)",
            backdropFilter: "blur(6px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: theme === "dark" ? "#1e1e1e" : "#fff",
              color: theme === "dark" ? "#fff" : "#111",
              padding: 20,
              borderRadius: 12,
              minWidth: 280,
              position: "relative",
              boxShadow: theme === "dark"
                ? "0 0 12px rgba(255,255,255,0.1)"
                : "0 0 12px rgba(0,0,0,0.2)",
            }}
          >
            <button
              onClick={() => setModalOpen(false)}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                background: "transparent",
                border: "none",
                color: theme === "dark" ? "#fff" : "#111",
                fontSize: 18,
                cursor: "pointer",
              }}
            >
              ×
            </button>

            <h4 style={{ marginTop: 0 }}>{T.addFavStop}</h4>

            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>{T.line}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {["purple", "yellow", "green"].map((line) => {
                  const active = selectedLine === line;
                  const color = lineColors[line] || "#888";
                  const label =
                    line === "purple" ? T.purpleLine : line === "yellow" ? T.yellowLine : T.greenLine;
                  return (
                    <button
                      key={line}
                      onClick={() => {
                        setSelectedLine(line);
                        setSelectedStop("");
                        setSelectedTowards("");
                      }}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: active ? `2px solid ${color}` : "1px solid #ccc",
                        background: active ? "#f7faff" : "#fff",
                        color: "#111",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: color,
                          display: "inline-block",
                          marginRight: 6,
                        }}
                      />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stop dropdown */}
            <label style={{ display: "block", marginBottom: 12 }}>
              {T.stop}:{" "}
              <select
                value={selectedStop}
                onChange={(e) => setSelectedStop(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: 6,
                  padding: "8px",
                  borderRadius: 6,
                  border: theme === "dark" ? "1px solid #444" : "1px solid #ccc",
                  background: theme === "dark" ? "#2a2a2a" : "#fff",
                  color: theme === "dark" ? "#fff" : "#111",
                }}
              >
                <option value="">{T.select}</option>
                {(stopsByLine[selectedLine] || []).map((stop) => (
                  <option key={stop.name} value={stop.name}>
                    {getStationLabel(stop)}
                  </option>
                ))}
              </select>
            </label>

            {/* Towards dropdown */}
            <label style={{ display: "block", marginBottom: 12 }}>
              Towards:{" "}
              <select
                value={selectedTowards}
                onChange={(e) => setSelectedTowards(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: 6,
                  padding: "8px",
                  borderRadius: 6,
                  border: theme === "dark" ? "1px solid #444" : "1px solid #ccc",
                  background: theme === "dark" ? "#2a2a2a" : "#fff",
                  color: theme === "dark" ? "#fff" : "#111",
                }}
              >
                <option value="">Select direction</option>
                {(() => {
                  const stations = stopsByLine[selectedLine] || [];
                  if (stations.length === 0) return null;
                  const first = stations[0];
                  const last = stations[stations.length - 1];
                  return (
                    <>
                      <option value={first.name}>Towards {getStationLabel(first)}</option>
                      <option value={last.name}>Towards {getStationLabel(last)}</option>
                    </>
                  );
                })()}
              </select>
            </label>

            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: theme === "dark" ? "#333" : "#e0e0e0",
                  color: theme === "dark" ? "#fff" : "#111",
                  cursor: "pointer",
                }}
              >
                {T.cancel}
              </button>
              <button
                onClick={addFavorite}
                disabled={!selectedStop || !selectedTowards}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "none",
                  background:
                    !selectedStop || !selectedTowards
                      ? theme === "dark"
                        ? "#555"
                        : "#ccc"
                      : theme === "dark"
                        ? "#4d91ff"
                        : "#1976d2",
                  color: "#fff",
                  cursor: !selectedStop || !selectedTowards ? "not-allowed" : "pointer",
                }}
              >
                {T.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
